import { preposiciones, conjunciones, articulos } from "./lucene-utils";

/**
 * Normaliza un texto: convierte a minúsculas, elimina acentos y diacríticos.
 * @param text El texto a normalizar.
 * @returns El texto normalizado.
 */
export function normalizeText(text: unknown): string {
  if (typeof text !== "string") return  String(text);
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, ""); // elimina los acentos
}

/**
 * Determina si una cadena es un array de strings.
 * @param str La cadena a verificar.
 * @returns `true` si es un array de strings, `false` en caso contrario.
 */
export function isStringArray(str: string): boolean {
  try {
    const parsed = JSON.parse(str);
    return Array.isArray(parsed) && parsed.every(item => typeof item === 'string');
  } catch {
    return false;
  }
}

/**
 * Capitaliza un texto: pone la primera letra en mayúscula
 * y el resto en minúsculas. Si el texto es null, undefined
 * o cadena vacía, devuelve una cadena vacía.
 * @param text El texto a capitalizar.
 * @returns El texto capitalizado.
 */
export function capitalize(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : '';
}

/**
 * Pasa a mayúsculas un texto. 
 * Si el texto es null, undefined o cadena vacía, devuelve una cadena vacía.
 * @param text El texto.
 * @returns El texto en mayúsculas.
 */
export function toUpperCase(text: string): string {
  return text ? text.trim().toUpperCase() : '';
}

/**
 * Pasa a minusculas un texto. 
 * Si el texto es null, undefined o cadena vacía, devuelve una cadena vacía.
 * @param text El texto.
 * @returns El texto en minusculas.
 */
export function toLowerCase(text: string): string {
  return text ? text.trim().toLowerCase() : '';
}


/**
 * Construye una cadena formateada uniendo varios elementos con un separador.
 *
 * - Elimina valores nulos, indefinidos o vacíos.
 * - Convierte números a texto automáticamente.
 * - Limpia espacios en blanco al inicio y al final de cada elemento.
 * - Evita que aparezcan separadores de más cuando faltan datos.
 *
 * @param parts - Lista de valores a unir (strings, números o valores vacíos).
 * @param separator - Cadena usada para unir los elementos (coma, espacio, etc.).
 * @returns Una cadena formateada con los elementos válidos unidos por el separador.
 */
export function joinClean(
  parts: Array<string | number | null | undefined>,
  separator: string = " "
): string {
  return parts
    .filter(p => p !== null && p !== undefined && `${p}`.trim() !== "")
    .map(p => `${p}`.trim())
    .join(separator);
}

/**
 * Función para convertir un string (key=value;) en objeto
 * @param str El string a convertir, en formato "key=value;"
 * @returns Un objeto con las claves y valores extraídos del string
 */
export function parseStringToObject(str?: string): Record<string, string> {
  const obj: Record<string, string> = {};
  if (!str) return obj; // si str es undefined o vacío, devolvemos objeto vacío

  str.split(";").forEach(part => {
    if (part.trim() !== "") {
      const [key, val] = part.split("=");
      if (key) {
        obj[key] = val ?? "";
      }
    }
  });
  return obj;
};

/**
 * Divide un apellido compuesto en dos partes, considerando preposiciones, conjunciones y artículos.
 * @param lastname El apellido a dividir.
 * @returns Un objeto con las dos partes del apellido.
 */
export function getSurnames(lastname: string): { surname1: string; surname2: string } {
  const partes = lastname.trim().split(/\s+/);
  const lowerPartes = partes.map(p => p.toLowerCase());

  const esIntroductoria = (word: string) =>
    preposiciones.includes(word) || conjunciones.includes(word) || articulos.includes(word);

  let surname1: string[] = [];
  let surname2: string[] = [];

  for (let i = 0; i < partes.length; i++) {
    const word = lowerPartes[i];

    if (surname2.length === 0) {
      surname1.push(partes[i]);

      if (!esIntroductoria(word)) {
        surname2 = partes.slice(i + 1);
        break;
      }
    } else {
      surname2.push(partes[i]);
    }
  }

  return {
    surname1: surname1.join(' '),
    surname2: surname2.join(' ')
  };
}